import { useEffect, useState, type ReactNode } from "react";
import { Modal } from "../Modal";
import { PhotoInput } from "../PhotoInput";
import { BarcodeField } from "../BarcodeField";
import { DraftThumb } from "./DraftThumb";
import { SupplierPicture } from "./SupplierPicture";
import type { CatalogDraft, Color, ItemCategory } from "../../api/types";

export type DraftRowPatch = {
  name?: string;
  categoryId?: string | null;
  colorId?: string | null;
  barcodeRaw?: string | null;
};

/**
 * G-58 — one draft as the review pass sees it: supplier picture · code ·
 * name · category · colour · price once received · barcode. Inline edits save
 * as you go.
 *
 * G-129: extracted from `DraftReviewTable` so the paper page shows a line the
 * app could not create by itself with THE SAME row — photo, suggestion,
 * barcode picture, the same-name twin — instead of a second form. The caller
 * supplies the cells after the barcode (`extraCells`) and the action buttons
 * (`actions`); the staging page keeps Promote / Delete.
 */
export function DraftReviewRow({
  draft,
  categories,
  colors,
  busy,
  onPatch,
  onPhoto,
  extraCells,
  actions,
  nameNote,
}: {
  draft: CatalogDraft;
  categories: ItemCategory[];
  colors: Color[];
  busy: boolean;
  onPatch: (draft: CatalogDraft, patch: DraftRowPatch) => Promise<void>;
  /** Saves a photo taken or chosen for this draft. */
  onPhoto: (draftId: string, file: File | null) => Promise<void> | void;
  extraCells?: ReactNode;
  actions: ReactNode;
  /** G-129: a caller's own sentence under the name — the paper page passes the matcher's reason. */
  nameNote?: ReactNode;
}) {
  const [name, setName] = useState(draft.name);
  const [barcode, setBarcode] = useState(draft.barcodeRaw ?? "");
  const [showBarcodePicture, setShowBarcodePicture] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  useEffect(() => setName(draft.name), [draft.name]);
  useEffect(() => setBarcode(draft.barcodeRaw ?? ""), [draft.barcodeRaw]);

  const suggested = draft.suggestedCategoryCode
    ? categories.find((c) => c.code === draft.suggestedCategoryCode)
    : undefined;
  const effectiveCategoryId = draft.categoryId ?? suggested?.id ?? "";
  // G-58 follow-up: the colour from the description's last two words,
  // pre-selected the same way the category is.
  const suggestedColor = draft.suggestedColorName
    ? colors.find(
        (c) => c.name.toLowerCase() === draft.suggestedColorName?.toLowerCase(),
      )
    : undefined;
  const effectiveColorId = draft.colorId ?? suggestedColor?.id ?? "";

  return (
    <tr className={busy ? "is-busy" : undefined}>
      <td className="draft-review__thumb-cell">
        {/* The draft's OWN photo when it has one, the supplier's picture when
            it does not, and an invitation to add one when there is neither.
            Tapping any of the three opens the same camera/file chooser — on a
            tablet walking the shop that is the whole interaction. */}
        <button
          type="button"
          className="draft-review__photo-btn"
          onClick={() => setPhotoOpen(true)}
          disabled={busy}
          title={draft.imageKey ? "Replace this photo" : "Add a photo"}
          aria-label={
            draft.imageKey
              ? `Replace the photo for ${draft.name}`
              : `Add a photo for ${draft.name}`
          }
        >
          {draft.imageKey ? (
            <DraftThumb draft={draft} />
          ) : draft.supplierThumbnailKey ? (
            <SupplierPicture
              draftId={draft.id}
              kind="thumbnail"
              alt={`Supplier photo of ${draft.name}`}
              className="draft-review__thumb"
            />
          ) : (
            <span aria-hidden className="draft-review__thumb draft-review__thumb--empty">
              +
            </span>
          )}
        </button>
      </td>
      <td>
        <code>{draft.supplierSku ?? "—"}</code>
        <div className="field-hint">{draft.nameRaw !== draft.name ? draft.nameRaw : ""}</div>
      </td>
      <td>
        <input
          aria-label={`Name for ${draft.supplierSku ?? draft.nameRaw}`}
          value={name}
          maxLength={200}
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => {
            if (name.trim() && name.trim() !== draft.name) {
              void onPatch(draft, { name: name.trim() });
            }
          }}
        />
        {/* G-129: why the app left this one to a person — an active item
            already carries the name. Pick it as the existing item, or create
            anyway if it really is a different product. */}
        {nameNote ? <div className="field-hint text-danger">{nameNote}</div> : null}
        {draft.nameTwins.length > 0 ? (
          <div className="field-hint text-danger">
            {draft.nameTwins.length === 1
              ? `An item is already called «${draft.nameTwins[0].name}» — pick it as the existing item, or create anyway if this is a different product.`
              : `${draft.nameTwins.length} items are already called «${draft.nameTwins[0].name}» — pick the right one as the existing item, or create anyway if this is a different product.`}
          </div>
        ) : null}
      </td>
      <td>
        <select
          aria-label={`Category for ${draft.name}`}
          value={effectiveCategoryId}
          disabled={busy}
          onChange={(e) => void onPatch(draft, { categoryId: e.target.value || null })}
        >
          <option value="">Choose…</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
        {!draft.categoryId && suggested ? (
          <div className="field-hint">suggested</div>
        ) : !draft.categoryId ? (
          <div className="field-hint text-danger">no suggestion — pick one</div>
        ) : null}
      </td>
      <td>
        <select
          aria-label={`Colour for ${draft.name}`}
          value={effectiveColorId}
          disabled={busy}
          onChange={(e) => void onPatch(draft, { colorId: e.target.value || null })}
        >
          <option value="">— None —</option>
          {colors.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        {!draft.colorId && suggestedColor ? (
          <div className="field-hint">suggested</div>
        ) : !draft.colorId && draft.suggestedColorName ? (
          <div className="field-hint text-danger">
            &ldquo;{draft.suggestedColorName}&rdquo; is not in the colour list
          </div>
        ) : null}
      </td>
      <td className="draft-review__price">
        {draft.suggestedSalePriceEur ? `€${draft.suggestedSalePriceEur}` : "—"}
        {draft.sourceUnitCostEur ? (
          <div className="field-hint">cost €{Number(draft.sourceUnitCostEur).toFixed(2)}</div>
        ) : null}
      </td>
      <td className="draft-review__barcode-cell">
        <BarcodeField
          id={`review-barcode-${draft.id}`}
          label="Barcode"
          value={barcode}
          onChange={setBarcode}
          onScanDetected={(code) => void onPatch(draft, { barcodeRaw: code })}
        />
        {barcode.trim() !== (draft.barcodeRaw ?? "") ? (
          <button
            type="button"
            className="button plain"
            disabled={busy}
            onClick={() => void onPatch(draft, { barcodeRaw: barcode.trim() || null })}
          >
            Save barcode
          </button>
        ) : null}
        {draft.barcodeImageKey && !draft.barcodeRaw ? (
          <button
            type="button"
            className="button plain"
            onClick={() => setShowBarcodePicture((v) => !v)}
          >
            {showBarcodePicture ? "Hide picture" : "Show barcode picture"}
          </button>
        ) : null}
        {showBarcodePicture && draft.barcodeImageKey ? (
          <SupplierPicture
            draftId={draft.id}
            kind="barcode"
            alt={`Barcode picture for ${draft.name}`}
            className="draft-review__barcode-picture"
          />
        ) : null}
      </td>
      {extraCells}
      <td className="draft-review__actions">
        {photoOpen ? (
          <Modal title={`Photo — ${draft.name}`} onClose={() => setPhotoOpen(false)}>
            {/* PhotoInput already IS the two-choices-plus-toggle control the
                owner asked for: "Choose file", "Take a photo" (rear camera on
                a tablet) and "Auto-remove background". */}
            <PhotoInput
              value={null}
              label={`Photo — ${draft.name}`}
              showRemoveButton={false}
              onChange={(file) => {
                if (!file) return;
                void onPhoto(draft.id, file);
                setPhotoOpen(false);
              }}
            />
          </Modal>
        ) : null}
        {actions}
      </td>
    </tr>
  );
}

/** A draft can be promoted once it has a category (chosen or suggested) and a name. */
export function draftCanPromote(draft: CatalogDraft, categories: ItemCategory[]): boolean {
  const suggested = draft.suggestedCategoryCode
    ? categories.some((c) => c.code === draft.suggestedCategoryCode)
    : false;
  return (Boolean(draft.categoryId) || suggested) && draft.name.trim().length > 0;
}
